"use client";

import { useTranslations } from "next-intl";

type PrivacySection = {
    title: string;
    text?: string;
    intro?: string;
    items?: string[];
};

export default function PrivacyPolicyPage() {
    const t = useTranslations("profile.privacy");
    const profile = useTranslations("profile");
    const sections: PrivacySection[] = [
        { title: t("pi_title"), items: t.raw("pi_items") },
        { title: t("geo_title"), text: t("geo_text") },
        { title: t("use_title"), items: t.raw("use_items") },
        { title: t("share_title"), intro: t("share_intro"), items: t.raw("share_items") },
        { title: t("protect_title"), text: t("protect_text") },
        { title: t("choice_title"), intro: t("choice_intro"), items: t.raw("choice_items") },
        { title: t("change_title"), text: t("change_text") },
    ];

    return (
        <main className="relative min-h-screen overflow-hidden bg-[#080d16] px-4 pb-20 pt-28 text-slate-100 sm:px-6 lg:px-10">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(52,211,153,0.13),transparent_28%),radial-gradient(circle_at_85%_10%,rgba(56,189,248,0.16),transparent_30%),linear-gradient(135deg,rgba(15,23,42,0.8),rgba(8,13,22,0.95))]" />
            <div className="relative mx-auto max-w-5xl">
                <header className="mb-8 rounded-3xl border border-cyan-300/20 bg-slate-900/70 p-7 shadow-2xl shadow-cyan-950/20 backdrop-blur sm:p-10">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-300">AfroChat</p>
                    <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">{profile("privacy_policy")}</h1>
                    <p className="mt-5 max-w-3xl text-base font-normal leading-7 text-slate-300 sm:text-lg">
                        {t("privacy_intro")}
                    </p>
                </header>

                <div className="grid gap-5 md:grid-cols-2">
                    {sections.map((section, index) => (
                        <section
                            key={section.title}
                            className={`rounded-3xl border border-white/10 bg-slate-900/65 p-6 shadow-xl backdrop-blur sm:p-8 ${index === 1 ? "border-emerald-300/25 bg-emerald-950/20" : ""}`}
                        >
                            <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                            {section.intro ? <p className="mt-4 leading-7 text-slate-300">{section.intro}</p> : null}
                            {section.text ? <p className="mt-4 leading-7 text-slate-300">{section.text}</p> : null}
                            {section.items ? (
                                <ul className="mt-4 space-y-3 text-slate-300">
                                    {section.items.map((item) => (
                                        <li key={item} className="flex gap-3 leading-7">
                                            <span aria-hidden className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </section>
                    ))}
                </div>

                <section className="mt-5 rounded-3xl border border-white/10 bg-slate-900/65 p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-white">{t("contact_title")}</h2>
                    <p className="mt-4 leading-7 text-slate-300">
                        {t("contact_text")} {" "}
                        <a className="font-medium text-cyan-300 underline decoration-cyan-300/40 underline-offset-4" href="mailto:contact-afrochat@a2sv.org">
                            contact-afrochat@a2sv.org
                        </a>
                    </p>
                </section>
            </div>
        </main>
    );
}
